/**
 * VedangJyotisham V6.2.0 — Muhurta Phase 16 Solar & Lunar Events Engine
 * Implements authoritative astronomical calculation and evaluation for:
 * - Sankranti (Sidereal Sun sign ingress, exact crossing timestamps, wrap-around, Punya Kaal)
 * - Solar and Lunar Eclipses (Astronomical detection, contact phases, Sutak periods)
 * - Activity-aware restrictions and visibility classification (Global event vs local visibility)
 */

import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).Observer ? _Astronomy : ((_Astronomy as any).default || _Astronomy);
import { getLahiriAyanamsha } from './vedicEngine';
import { getCanonicalCivilDayUtcBounds } from './panchangaEngine';

export interface SankrantiEvent {
  eventId: string;
  eventName: string;
  fromSign: string;
  toSign: string;
  ingressTimestamp: Date;
  punyaKaalStart: Date;
  punyaKaalEnd: Date;
  classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION';
  explanation: string;
}

export interface EclipseEvent {
  eventId: string;
  eventName: string;
  eclipseType: 'solar' | 'lunar';
  subtype: 'total' | 'partial' | 'penumbral' | 'annular';
  peakTimestamp: Date;
  globalStart?: Date;
  globalEnd?: Date;
  lunarContacts?: { p1: Date; u1?: Date; u2?: Date; maximum: Date; u3?: Date; u4?: Date; p4: Date };
  localContacts?: { first: Date; second?: Date; maximum: Date; third?: Date; fourth: Date };
  visibility: 'GLOBAL_ONLY' | 'LOCAL_VISIBLE' | 'LOCAL_NOT_VISIBLE' | 'LOCAL_VISIBILITY_UNCERTAIN' | 'UNKNOWN_OBSERVER';
  locallyApplicable: boolean;
  sutakStart?: Date;
  sutakEnd?: Date;
  classification: 'HARD_EXCLUSION' | 'SUPPORT' | 'INFORMATIONAL';
  explanation: string;
}

/** Location and fixed UTC offset required for local visibility and civil-day applicability. Omitted elevation assumes sea level. */
export interface Phase16Observer {
  latitude: number;
  longitude: number;
  timezoneOffsetMinutes: number;
  elevationMeters?: number;
}

export interface Phase16EvaluationResult {
  factorId: string;
  factorName: string;
  category: 'SANKRANTI' | 'ECLIPSE' | 'SUTAK';
  classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL';
  scoreImpact: number;
  description: string;
  evidence: {
    sankrantis: SankrantiEvent[];
    eclipses: EclipseEvent[];
    isSutakActive: boolean;
  };
}

/**
 * Calculates authoritative sidereal longitude of the Sun for a given date.
 */
function getSiderealSunLongitude(date: Date): number {
  const time = Astronomy.MakeTime(date);
  const sunEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true));
  const ayanamsha = getLahiriAyanamsha(time);
  let siderealLon = (sunEcl.elon - ayanamsha + 360) % 360;
  return siderealLon;
}

/**
 * Detects sidereal Sun sign ingress (Sankranti) around a test date.
 */
export function detectSankrantiAroundDate(testDate: Date): SankrantiEvent | null {
  const start = new Date(testDate.getTime() - 36 * 3600 * 1000);
  const end = new Date(testDate.getTime() + 36 * 3600 * 1000);

  const startLon = getSiderealSunLongitude(start);
  const startSign = Math.floor(startLon / 30);
  const endLon = getSiderealSunLongitude(end);
  const endSign = Math.floor(endLon / 30);

  if (startSign !== endSign || Math.abs(endLon - startLon) > 25) {
    let low = new Date(start.getTime());
    let high = new Date(end.getTime());
    let exactTime = new Date(testDate.getTime());

    for (let i = 0; i < 15; i++) {
      const midTime = new Date((low.getTime() + high.getTime()) / 2);
      const midLon = getSiderealSunLongitude(midTime);
      const midSign = Math.floor(midLon / 30);

      if (midSign === startSign) {
        low = midTime;
      } else {
        high = midTime;
        exactTime = midTime;
      }
    }

    const targetSignIndex = Math.floor(getSiderealSunLongitude(exactTime) / 30);
    const signNames = [
      'Mesha', 'Vrishabha', 'Mithuna', 'Karka', 'Simha', 'Kanya',
      'Tula', 'Vrishchika', 'Dhanu', 'Makara', 'Kumbha', 'Meena'
    ];
    const toSign = signNames[targetSignIndex];
    const fromSign = signNames[(targetSignIndex - 1 + 12) % 12];

    const punyaStart = new Date(exactTime.getTime() - 6 * 3600 * 1000);
    const punyaEnd = new Date(exactTime.getTime() + 6 * 3600 * 1000);

    return {
      eventId: `sankranti_${toSign.toLowerCase()}_${exactTime.toISOString().slice(0, 10)}`,
      eventName: `${toSign} Sankranti`,
      fromSign,
      toSign,
      ingressTimestamp: exactTime,
      punyaKaalStart: punyaStart,
      punyaKaalEnd: punyaEnd,
      classification: 'WARNING',
      explanation: `${toSign} Sankranti ingress verified at ${exactTime.toUTCString()}. Punya Kaal active for charity and spiritual rites.`
    };
  }

  return null;
}

/**
 * Detects astronomical eclipses near test date.
 */
export function detectEclipsesAroundDate(testDate: Date, observer?: Phase16Observer): EclipseEvent[] {
  const events: EclipseEvent[] = [];
  const validObserver = observer && Number.isFinite(observer.latitude) && observer.latitude >= -90 && observer.latitude <= 90 &&
    Number.isFinite(observer.longitude) && observer.longitude >= -180 && observer.longitude <= 180 &&
    Number.isFinite(observer.timezoneOffsetMinutes) && Math.abs(observer.timezoneOffsetMinutes) <= 14 * 60;
  const day = validObserver ? getCanonicalCivilDayUtcBounds(testDate, observer.timezoneOffsetMinutes) : undefined;
  // Search before the requested civil day so a peak just outside its bounds does
  // not hide an eclipse whose contact interval overlaps the day.
  const searchStart = new Date((day?.startUtc ?? testDate).getTime() - 2 * 24 * 3600 * 1000);
  const startTime = Astronomy.MakeTime(searchStart);

  try {
    const lunar = Astronomy.SearchLunarEclipse(startTime);
    if (lunar) {
      const peak = new Date(lunar.peak.date);
      const globalStart = new Date(peak.getTime() - lunar.sd_penum * 60_000);
      const globalEnd = new Date(peak.getTime() + lunar.sd_penum * 60_000);
      const localSutakStart = validObserver ? new Date(globalStart.getTime() - 9 * 3600 * 1000) : undefined;
      const applicableStart = localSutakStart ?? globalStart;
      const overlapsDay = day ? applicableStart < day.endUtc && globalEnd >= day.startUtc : Math.abs(peak.getTime() - testDate.getTime()) < 5 * 24 * 3600 * 1000;
      if (overlapsDay) {
        const lunarVisibility = validObserver ? moonVisibilityDuring(globalStart, globalEnd, observer) : undefined;
        const visibility: EclipseEvent['visibility'] = !validObserver ? 'UNKNOWN_OBSERVER' : lunarVisibility!;
        const localVisible = visibility === 'LOCAL_VISIBLE';
        events.push({
          eventId: `lunar_eclipse_${peak.toISOString().slice(0, 10)}`,
          eventName: 'Lunar Eclipse (Chandra Grahana)',
          eclipseType: 'lunar',
          subtype: lunar.kind as EclipseEvent['subtype'],
          peakTimestamp: peak,
          globalStart,
          globalEnd,
          lunarContacts: {
            p1: globalStart,
            u1: lunar.sd_partial > 0 ? new Date(peak.getTime() - lunar.sd_partial * 60_000) : undefined,
            u2: lunar.sd_total > 0 ? new Date(peak.getTime() - lunar.sd_total * 60_000) : undefined,
            maximum: peak,
            u3: lunar.sd_total > 0 ? new Date(peak.getTime() + lunar.sd_total * 60_000) : undefined,
            u4: lunar.sd_partial > 0 ? new Date(peak.getTime() + lunar.sd_partial * 60_000) : undefined,
            p4: globalEnd
          },
          visibility,
          locallyApplicable: visibility === 'LOCAL_VISIBLE',
          sutakStart: localVisible ? localSutakStart : undefined,
          sutakEnd: localVisible ? globalEnd : undefined,
          classification: localVisible ? 'HARD_EXCLUSION' : 'INFORMATIONAL',
          explanation: `Global ${lunar.kind} lunar eclipse P1 ${globalStart.toISOString()}, maximum ${peak.toISOString()}, P4 ${globalEnd.toISOString()}; visibility ${visibility}. Astronomy Engine does not provide observer-corrected lunar contact times; horizon visibility is screened at two-minute intervals.`
        });
      }
    }
  } catch {
    // Preserve absence of evidence; do not infer occurrence or visibility.
  }

  try {
    const solar = Astronomy.SearchGlobalSolarEclipse(startTime);
    const peak = new Date(solar.peak.date);
    let local: _Astronomy.LocalSolarEclipseInfo | undefined;
    if (validObserver) {
      const localSearch = Astronomy.SearchLocalSolarEclipse(
        Astronomy.MakeTime(new Date(peak.getTime() - 24 * 3600 * 1000)),
        new Astronomy.Observer(observer.latitude, observer.longitude, Math.max(0, observer.elevationMeters ?? 0))
      );
      if (Math.abs(localSearch.peak.time.date.getTime() - peak.getTime()) <= 24 * 3600 * 1000) local = localSearch;
    }
    const localStart = local?.partial_begin.time.date;
    const localEnd = local?.partial_end.time.date;
    const localSutakStart = localStart ? new Date(localStart.getTime() - 12 * 3600 * 1000) : undefined;
    const applicableStart = localSutakStart ?? localStart;
    const overlapsDay = day
      ? (applicableStart && localEnd ? applicableStart < day.endUtc && localEnd >= day.startUtc : peak >= day.startUtc && peak < day.endUtc)
      : Math.abs(peak.getTime() - testDate.getTime()) < 5 * 24 * 3600 * 1000;
    if (overlapsDay) {
      const nearSolarHorizon = Boolean(local && Math.abs(local.peak.altitude) <= 0.25);
      const localVisible = Boolean(local && !nearSolarHorizon && [local.partial_begin, local.total_begin, local.peak, local.total_end, local.partial_end]
        .some(contact => contact && contact.altitude >= 0));
      const visibility: EclipseEvent['visibility'] = !validObserver ? 'UNKNOWN_OBSERVER' : !local ? 'GLOBAL_ONLY' : nearSolarHorizon ? 'LOCAL_VISIBILITY_UNCERTAIN' : localVisible ? 'LOCAL_VISIBLE' : 'LOCAL_NOT_VISIBLE';
      events.push({
        eventId: `solar_eclipse_${peak.toISOString().slice(0, 10)}`,
        eventName: 'Solar Eclipse (Surya Grahana)',
        eclipseType: 'solar',
        subtype: solar.kind as EclipseEvent['subtype'],
        peakTimestamp: peak,
        localContacts: local ? {
          first: local.partial_begin.time.date,
          second: local.total_begin?.time.date,
          maximum: local.peak.time.date,
          third: local.total_end?.time.date,
          fourth: local.partial_end.time.date
        } : undefined,
        visibility,
        locallyApplicable: visibility === 'LOCAL_VISIBLE',
        sutakStart: localVisible ? localSutakStart : undefined,
        sutakEnd: localVisible ? localEnd : undefined,
        classification: localVisible ? 'HARD_EXCLUSION' : 'INFORMATIONAL',
        explanation: `Global ${solar.kind} solar eclipse peak ${peak.toISOString()}; local contact data ${localStart?.toISOString() ?? 'unavailable'} to ${localEnd?.toISOString() ?? 'unavailable'}; visibility ${visibility}. Astronomy Engine does not expose global solar contacts.`
      });
    }
  } catch {
    // Preserve absence of evidence; do not infer occurrence or visibility.
  }
  return events;
}

function moonVisibilityDuring(start: Date, end: Date, observer: Phase16Observer): 'LOCAL_VISIBLE' | 'LOCAL_NOT_VISIBLE' | 'LOCAL_VISIBILITY_UNCERTAIN' {
  const location = new Astronomy.Observer(observer.latitude, observer.longitude, Math.max(0, observer.elevationMeters ?? 0));
  const stepMs = 2 * 60_000;
  let maxAltitude = -90;
  for (let ms = start.getTime(); ms <= end.getTime(); ms += stepMs) {
    const time = Astronomy.MakeTime(new Date(ms));
    const equatorial = Astronomy.Equator(Astronomy.Body.Moon, time, location, true, true);
    maxAltitude = Math.max(maxAltitude, Astronomy.Horizon(time, location, equatorial.ra, equatorial.dec, 'normal').altitude);
  }
  // Avoid claiming visibility/non-visibility at a grazing horizon where a coarse
  // deterministic sample and atmospheric refraction can change the result.
  if (Math.abs(maxAltitude) <= 0.25) return 'LOCAL_VISIBILITY_UNCERTAIN';
  return maxAltitude > 0 ? 'LOCAL_VISIBLE' : 'LOCAL_NOT_VISIBLE';
}

/**
 * Evaluates Phase 16 Solar & Lunar event factors for a given date and activity.
 */
export function evaluatePhase16SolarLunarRules(
  testDate: Date,
  activityName: string,
  observer?: Phase16Observer
): Phase16EvaluationResult {
  const sankranti = detectSankrantiAroundDate(testDate);
  const eclipses = detectEclipsesAroundDate(testDate, observer);

  let classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL' = 'NEUTRAL';
  let scoreImpact = 0;
  let description = 'No restrictive solar or lunar event or eclipse active.';

  const isSpiritual = activityName.toLowerCase().includes('spiritual') || activityName.toLowerCase().includes('sadhana');

  let isSutakActive = false;
  for (const ec of eclipses) {
    if (ec.locallyApplicable && ec.sutakStart && ec.sutakEnd && testDate.getTime() >= ec.sutakStart.getTime() && testDate.getTime() <= ec.sutakEnd.getTime()) {
      isSutakActive = true;
      if (isSpiritual) {
        classification = 'SUPPORT';
        scoreImpact = 25;
        description = `Sutak / Eclipse period active. Highly exalted for Mantra Japa and spiritual sadhana.`;
      } else {
        classification = 'HARD_EXCLUSION';
        scoreImpact = -50;
        description = `⛔ Eclipse & Sutak Period Active: Strictly prohibited for material commerce, travel, and samskaras.`;
      }
    }
  }

  if (!isSutakActive && sankranti) {
    const isPunyaKaal = testDate.getTime() >= sankranti.punyaKaalStart.getTime() && testDate.getTime() <= sankranti.punyaKaalEnd.getTime();
    if (isPunyaKaal) {
      classification = 'SUPPORT';
      scoreImpact = 15;
      description = `Punya Kaal active for ${sankranti.eventName}. Highly auspicious for charity, bath, and spiritual rites.`;
    } else {
      classification = 'WARNING';
      scoreImpact = -10;
      description = `${sankranti.eventName} transition moment requires caution for new material worldly ventures.`;
    }
  }

  if (!isSutakActive && !sankranti && eclipses.length > 0) {
    classification = 'INFORMATIONAL';
    scoreImpact = 0;
    description = 'A global eclipse was found, but local visibility and applicability are absent or not established for this observer.';
  }

  return {
    factorId: 'FACT_PHASE16_SOLAR_LUNAR',
    factorName: 'Phase 16 Solar & Lunar Events (Sankranti & Eclipses)',
    category: eclipses.length > 0 ? 'ECLIPSE' : 'SANKRANTI',
    classification,
    scoreImpact,
    description,
    evidence: {
      sankrantis: sankranti ? [sankranti] : [],
      eclipses,
      isSutakActive
    }
  };
}
